/**
 * @file useKeyboardInset.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-11
 * @modified 2026-09-11
 * @description How much of the screen the on-screen keyboard is covering, as a
 *              CSS variable.
 *
 *              `window.innerHeight` does not change when a phone keyboard opens
 *              — the layout viewport is still the full screen, and everything
 *              measured against it happily positions itself underneath the
 *              keyboard. `visualViewport` is the part actually visible, so the
 *              difference between the two IS the keyboard.
 *
 *              Published as `--kb-inset` on <html> rather than returned to a
 *              caller: the thing that needs it is a dropdown menu's CSS, and
 *              every dropdown in the app would otherwise have to be handed the
 *              value and made to apply it inline. One listener, one variable,
 *              and any rule anywhere can say `bottom: var(--kb-inset)`.
 *
 *              Reads 0px on a desktop browser and on any browser without
 *              visualViewport, so a rule using it is safe unconditionally.
 */

import { useEffect } from 'react';

export default function useKeyboardInset() {
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const root = document.documentElement;

    const apply = () => {
      if (!vv) { root.style.setProperty('--kb-inset', '0px'); return; }
      /*
       * offsetTop matters as well as height: iOS scrolls the visual viewport up
       * to keep the focused field in view, and without it the inset reads as
       * the keyboard height PLUS however far the page was pushed, which lifts
       * the sheet well clear of the keyboard instead of resting on it.
       */
      const covered = window.innerHeight - vv.height - vv.offsetTop;
      // Small negatives and jitter are normal as the keyboard animates.
      root.style.setProperty('--kb-inset', `${Math.max(0, Math.round(covered))}px`);
    };

    apply();
    if (!vv) return undefined;

    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      root.style.removeProperty('--kb-inset');
    };
  }, []);
}
